import useDiscoveryFetch from './useDiscoveryFetch';
import { useRef, useState, useEffect, useMemo } from 'react';
import { Table, Form, Row, Col, Card, Modal, Button } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Pagination from 'react-bootstrap/Pagination';
import { useMediaQuery } from 'react-responsive';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import api from '../../api/axiosInstance';
import { trackEvent } from '../../services/analytics/analyticsService';
import { ANALYTICS_EVENTS } from '../../services/analytics/analyticsEvents';
import defaultLogo from '../../assets/img/ic-img.svg';
import defaultCompanyMarkerLogo from '../../assets/img/ic-building-company.svg';
import Sidebar from '../../Sidebar';
import Breadcrumb from '../../Breadcrumb';
import DiscoveryMap from './DiscoveryMap';
import DiscoveryFilterBar from './DiscoveryFilterBar';
import DiscoveryEmptyState from './DiscoveryEmptyState';
import MobileDiscoveryHeading from './MobileDiscoveryHeading';
import MobileDiscoverySearchToolbar from './MobileDiscoverySearchToolbar';
import MobileDiscoveryViewSwitch from './MobileDiscoveryViewSwitch';
import MobileDiscoveryCompanyCard from './MobileDiscoveryCompanyCard';
import MobileAppliedChips from './MobileAppliedChips';
import MobileLoadMoreFooter from './MobileLoadMoreFooter';
import useMobileLoadMore from './useMobileLoadMore';
import useMobileDiscoveryNavDocking from './useMobileDiscoveryNavDocking';
import { captureDiscoveryFilters, buildDiscoveryFilterChipLabels } from './discoveryMobileFilters';
import DiscoveryDesktopAiSlot from './DiscoveryDesktopAiSlot';
import NavTabs from '../../NavTabs';
import CountryDetailMobileTabs from './countryDetail/CountryDetailMobileTabs';
import getCountryDetailNavItems from './countryDetail/countryDetailNavItems';
import icFilter from '../../assets/img/ic-filter.svg';
import icSort from '../../assets/img/ic-sort.svg';
import icJobSearch from '../../assets/img/ic-job-search.svg';
import icStar from '../../assets/img/ic-star.svg';
import icLink from '../../assets/img/ic-link-square.svg';
import icHeartEmpty from '../../assets/img/ic-heart-empty.svg';
import icHeartFull from '../../assets/img/ic-heart-full.svg';
import { useMobileAiPageContext } from '../../MobileAiAssistantContext';
import { getDiscoveryAiSuggestions } from '../../utils/discoveryAiSuggestions';

const getCompanySizeBucket = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw || raw === 'n/a' || raw === 'na' || raw === '-') return '';
    const compact = raw.replace(/employees?/g, '').replace(/\s+/g, ' ').trim();
    if (compact.includes('several 1000')) return '1,001 - 5,000 employees';
    if (compact.includes('under 30')) return '11 - 50 employees';
    if (compact.includes('over 100000')) return 'over 100000';
    const rangeMatch = compact.match(/(\d[\d,.\s]*)\s*[-–—]\s*(\d[\d,.\s]*)/);
    if (rangeMatch) {
        const min = Number(rangeMatch[1].replace(/[^\d]/g, ''));
        const max = Number(rangeMatch[2].replace(/[^\d]/g, ''));
        if (!Number.isNaN(min) && !Number.isNaN(max)) {
            if (max <= 10) return '1 - 10 employees';
            if (min >= 11 && max <= 50) return '11 - 50 employees';
            if (min >= 51 && max <= 200) return '51 - 200 employees';
            if (min >= 201 && max <= 500) return '201 - 500 employees';
            if (min >= 501 && max <= 1000) return '501 - 1,000 employees';
            if (min >= 1001 && max <= 5000) return '1,001 - 5,000 employees';
            if (min >= 5001 && max <= 10000) return '5,001 - 10,000 employees';
            if (min >= 10001 && max <= 50000) return '10001 - 50000 employees';
            if (min >= 50001 && max <= 100000) return '50001 - 100000';
            if (min > 100000) return 'over 100000';
            const avg = (min + max) / 2;
            if (avg <= 10) return '1 - 10 employees';
            if (avg <= 50) return '11 - 50 employees';
            if (avg <= 200) return '51 - 200 employees';
            if (avg <= 500) return '201 - 500 employees';
            if (avg <= 1000) return '501 - 1,000 employees';
            if (avg <= 5000) return '1,001 - 5,000 employees';
            if (avg <= 10000) return '5,001 - 10,000 employees';
            if (avg <= 50000) return '10001 - 50000 employees';
            if (avg <= 100000) return '50001 - 100000';
            return 'over 100000';
        }
    }
    const numericValue = Number(compact.replace(/[^\d]/g, ''));
    if (!Number.isNaN(numericValue) && numericValue > 0) {
        if (numericValue <= 10) return '1 - 10 employees';
        if (numericValue <= 50) return '11 - 50 employees';
        if (numericValue <= 200) return '51 - 200 employees';
        if (numericValue <= 500) return '201 - 500 employees';
        if (numericValue <= 1000) return '501 - 1,000 employees';
        if (numericValue <= 5000) return '1,001 - 5,000 employees';
        if (numericValue <= 10000) return '5,001 - 10,000 employees';
        if (numericValue <= 50000) return '10001 - 50000 employees';
        if (numericValue <= 100000) return '50001 - 100000';
        return 'over 100000';
    }
    return '';
};

const getCompanySizeSortValue = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw || raw === 'n/a' || raw === 'na' || raw === '-') return null;
    if (raw.includes('several 1000')) return 3000;
    if (raw.includes('under 30')) return 30;
    if (raw.includes('over 100000')) return 100001;
    const rangeMatch = raw.match(/(\d[\d,.\s]*)\s*[-–—]\s*(\d[\d,.\s]*)/);
    if (rangeMatch) {
        const min = Number(rangeMatch[1].replace(/[^\d]/g, ''));
        const max = Number(rangeMatch[2].replace(/[^\d]/g, ''));
        if (Number.isFinite(min) && Number.isFinite(max)) return (min + max) / 2;
    }
    const numericValue = Number(raw.replace(/[^\d]/g, ''));
    return Number.isFinite(numericValue) ? numericValue : null;
};

const mobileCompanySortDefinitions = {
    name: { defaultDirection: 'asc', type: 'text', value: (company) => String(company.title || '').trim() },
    city: { defaultDirection: 'asc', type: 'text', value: (company) => String(company.cityRegion || '').trim() },
    openJobs: { defaultDirection: 'desc', type: 'number', value: (company) => company.openJobsTotal },
    internships: { defaultDirection: 'desc', type: 'number', value: (company) => company.openJobsIntern },
    companySize: { defaultDirection: 'desc', type: 'number', value: (company) => getCompanySizeSortValue(company.globalSize || company.localSize) },
    rating: { defaultDirection: 'desc', type: 'number', value: (company) => Number(company.googleRating) || null },
    lastUpdate: { defaultDirection: 'desc', type: 'number', value: (company) => {
        const timestamp = new Date(company.latestDataUpdate || company.updatedAt || '').getTime();
        return Number.isFinite(timestamp) ? timestamp : null;
    } },
};

const getMobileSortedCompanies = (sourceCompanies, field, direction) => {
    const definition = mobileCompanySortDefinitions[field];
    if (!definition) return sourceCompanies;
    const multiplier = direction === 'asc' ? 1 : -1;
    return [...sourceCompanies].sort((a, b) => {
        const valueA = definition.value(a);
        const valueB = definition.value(b);
        const missingA = valueA === null || valueA === undefined || valueA === '';
        const missingB = valueB === null || valueB === undefined || valueB === '';
        if (missingA !== missingB) return missingA ? 1 : -1;
        if (missingA) return 0;
        const comparison = definition.type === 'text'
            ? String(valueA).toLowerCase().localeCompare(String(valueB).toLowerCase())
            : Number(valueA) - Number(valueB);
        return comparison * multiplier;
    });
};

const DiscoveryCountryCompanyList = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [countryName, setCountryName] = useState('');
    // Keep the last explicitly applied comparator when fresh API data arrives.
    const refreshSortRef = useRef(null);
    const [companies, setCompanies] = useState([]);
    const [industriesData, setIndustriesData] = useState([]);
    const [favorites, setFavorites] = useState(new Set());

    const [sortOrderName, setSortOrderName] = useState('asc');
    const [sortOrderCity, setSortOrderCity] = useState('asc');
    const [sortOrderIndustry, setSortOrderIndustry] = useState('asc');
    const [sortOrderGlobalSize, setSortOrderGlobalSize] = useState('asc');
    const [sortOrderRating, setSortOrderRating] = useState('asc');
    const [sortOrderLastUpdate, setSortOrderLastUpdate] = useState('asc');

    const [currentPage, setCurrentPage] = useState(1);
    const [companiesPerPage, setCompaniesPerPage] = useState(10);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [selectedStudyDirection, setSelectedStudyDirection] = useState('');
    const [selectedCompanySize, setSelectedCompanySize] = useState('');
    const [selectedCompanyRating, setSelectedCompanyRating] = useState('');
    const [selectedEnglishSpeaking, setSelectedEnglishSpeaking] = useState(null);
    const [selectedWorldTraveler, setSelectedWorldTraveler] = useState(null);
    const [selectedStudentJobAreas, setSelectedStudentJobAreas] = useState(null);
    const [selectedFavorites, setSelectedFavorites] = useState(null);
    const [savedFilterSets, setSavedFilterSets] = useState([]);
    const [selectedFilterSet, setSelectedFilterSet] = useState('default');
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [showMobileSort, setShowMobileSort] = useState(false);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const [draftSortField, setDraftSortField] = useState(null);
    const [draftSortDirection, setDraftSortDirection] = useState(null);
    const [appliedMobileFilters, setAppliedMobileFilters] = useState(() => captureDiscoveryFilters({}));

    // Matches the mobile card/filter-modal breakpoint used across Discovery pages.
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });
    const { isMobileNavDocked, mobileMapRowRef } = useMobileDiscoveryNavDocking();

    const industryNameByCode = useMemo(() => {
        const map = new Map();
        (industriesData || []).forEach((industry) => {
            if (industry?.code) {
                map.set(String(industry.code), industry.name || String(industry.code));
            }
        });
        return map;
    }, [industriesData]);

    const filteredCompanies = companies.filter((company) => {
        const normalizeStudyDirection = (val) =>
            String(val).trim()
                .replace(/^\d+(?:\.\d+)?\s*[-–—]?\s*/, '')
                .replace(/([.:])\s*\d+(?:\.\d+)?\s*[-–—]?\s*/g, '$1 ')
                .replace(/^\(\s*/, '')
                .replace(/\)\s*$/, '')
                .replace(/\s{2,}/g, ' ')
                .trim()
                .toLowerCase();

        const matchesSearch =
            !searchQuery ||
            company.title?.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesCity =
            !selectedCity ||
            String(company.cityRegion || '').trim() === String(selectedCity || '').trim();

        const matchesIndustry =
            !selectedIndustry ||
            (Array.isArray(company.industries) &&
                company.industries.some((entry) => entry?.industry === selectedIndustry));

        const matchesStudyDirection =
            !selectedStudyDirection ||
            (() => {
                const rawStudyDirections = company.studyFields?.preferredStudyDirections;
                if (Array.isArray(rawStudyDirections)) {
                    return rawStudyDirections.some(
                        (direction) => normalizeStudyDirection(direction) === normalizeStudyDirection(selectedStudyDirection)
                    );
                }
                if (typeof rawStudyDirections === 'string') {
                    return rawStudyDirections
                        .split(',')
                        .map((direction) => normalizeStudyDirection(direction))
                        .includes(normalizeStudyDirection(selectedStudyDirection));
                }
                return false;
            })();

        const companySizeBucket = getCompanySizeBucket(company.globalSize || company.localSize || '');
        const matchesCompanySize = !selectedCompanySize || companySizeBucket === selectedCompanySize;

        const companyRatingValue = Number(company.googleRating);
        const matchesCompanyRating =
            !selectedCompanyRating ||
            (!Number.isNaN(companyRatingValue) && companyRatingValue >= Number(selectedCompanyRating));

        const matchesEnglishSpeaking =
            selectedEnglishSpeaking === null || company.openedEnglishSpeaking === true;

        const matchesWorldTraveler =
            selectedWorldTraveler === null || company.openedWorldTraveler === true;

        const matchesStudentJobAreas =
            selectedStudentJobAreas === null ||
            (Array.isArray(company.studentJobAreas)
                ? company.studentJobAreas.length > 0
                : Boolean(company.studentJobAreas));

        const matchesFavorites =
            selectedFavorites === null || favorites.has(String(company._id));

        return (
            matchesSearch &&
            matchesCity &&
            matchesIndustry &&
            matchesStudyDirection &&
            matchesCompanySize &&
            matchesCompanyRating &&
            matchesEnglishSpeaking &&
            matchesWorldTraveler &&
            matchesStudentJobAreas &&
            matchesFavorites
        );
    });

    const indexOfLastCompany = currentPage * companiesPerPage;
    const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
    const currentCompanies = filteredCompanies.slice(indexOfFirstCompany, indexOfLastCompany);
    const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);
    const noCompanies = filteredCompanies.length === 0;
    const activeCityNames = useMemo(() => new Set(
        companies.map((company) => String(company.cityRegion || '').trim()).filter(Boolean)
    ), [companies]);
    const mobileSortedCompanies = sortField
        ? getMobileSortedCompanies(filteredCompanies, sortField, sortDirection)
        : filteredCompanies;
    const mobileFilterKey = JSON.stringify({
        countryId: id,
        searchQuery,
        selectedCity,
        selectedIndustry,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedFavorites,
        sortField,
        sortDirection,
    });
    const mobilePagination = useMobileLoadMore({
        items: mobileSortedCompanies,
        filterKey: mobileFilterKey,
        storageKey: `discovery-country-${id}-companies-mobile-list`,
        isMobile,
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [
        searchQuery,
        selectedCity,
        selectedIndustry,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedFavorites,
    ]);

    const buildCurrentFiltersPayload = () => ({
        searchQuery,
        selectedCity,
        selectedIndustry,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedFavorites,
    });

    const applySavedFilterSet = (filters = {}) => {
        setSearchQuery(filters.searchQuery || '');
        setSelectedCity(filters.selectedCity || '');
        setSelectedIndustry(filters.selectedIndustry || '');
        setSelectedStudyDirection(filters.selectedStudyDirection || '');
        setSelectedCompanySize(filters.selectedCompanySize || '');
        setSelectedCompanyRating(filters.selectedCompanyRating || '');
        setSelectedEnglishSpeaking(filters.selectedEnglishSpeaking === true ? true : null);
        setSelectedWorldTraveler(filters.selectedWorldTraveler === true ? true : null);
        setSelectedStudentJobAreas(filters.selectedStudentJobAreas === true ? true : null);
        setSelectedFavorites(filters.selectedFavorites === true ? true : null);
    };

    const handleSaveFilters = async (filterSetName) => {
        try {
            const trimmedName = String(filterSetName || '').trim();
            if (!trimmedName) return;
            const payload = {
                page: 'country-companies',
                name: trimmedName,
                filters: buildCurrentFiltersPayload(),
            };
            const res = await api.post('/api/discovery-filter-sets', payload);
            const savedFilterSet = res.data;
            setSavedFilterSets((prev) => {
                const withoutDuplicate = prev.filter(
                    (item) => item._id !== savedFilterSet._id && item.name !== savedFilterSet.name
                );
                return [savedFilterSet, ...withoutDuplicate];
            });
            setSelectedFilterSet(savedFilterSet._id);
        } catch (error) {
            console.error('Error saving discovery country company filter set:', error);
        }
    };

    const handleDeleteFilterSet = async (filterSetId) => {
        if (!filterSetId || filterSetId === 'default') return;
        try {
            await api.delete(`/api/discovery-filter-sets/${filterSetId}`);
            const wasSelected = String(selectedFilterSet) === String(filterSetId);
            setSavedFilterSets((prev) =>
                prev.filter((filterSet) => String(filterSet._id) !== String(filterSetId))
            );
            if (wasSelected) {
                setSelectedFilterSet('default');
                applySavedFilterSet({});
            }
        } catch (err) {
            console.error('Error deleting filter set:', err);
        }
    };

    const handleFilterSetChange = (value) => {
        setSelectedFilterSet(value);
        if (value === 'default') {
            applySavedFilterSet({});
            return;
        }
        const selectedSavedFilterSet = savedFilterSets.find((item) => item._id === value);
        if (selectedSavedFilterSet?.filters) {
            applySavedFilterSet(selectedSavedFilterSet.filters);
        }
    };

    useDiscoveryFetch(({ signal, isCurrent }) => {
        const fetchData = async () => {
            try {
                const [countryRes, companiesRes, industriesRes, filterSetsRes, currentUserRes] = await Promise.all([
                    api.get(`/api/discovery/countries/${id}`, { signal }),
                    api.get(`/api/discovery/countries/${id}/companies`, { signal }),
                    api.get('/api/nace', { signal }),
                    api.get('/api/discovery-filter-sets?page=country-companies', { signal }),
                    api.get('/api/users/me', { signal }),
                ]);
                if (!isCurrent()) return;

                const countryData = countryRes.data;
                setCountryName(countryData?.countryName || countryData?.country?.name || id);

                const safeCompanies = Array.isArray(companiesRes.data) ? companiesRes.data : [];
                const safeIndustries = Array.isArray(industriesRes.data) ? industriesRes.data : [];
                const safeFilterSets = Array.isArray(filterSetsRes.data) ? filterSetsRes.data : [];
                const safeFavoriteCompanies = Array.isArray(currentUserRes?.data?.favoriteCompanies)
                    ? currentUserRes.data.favoriteCompanies.map((companyId) => String(companyId))
                    : [];

                setCompanies(refreshSortRef.current ? [...safeCompanies].sort(refreshSortRef.current) : safeCompanies);
                setIndustriesData(safeIndustries);
                setSavedFilterSets(safeFilterSets);
                setFavorites(new Set(safeFavoriteCompanies));
            } catch (error) {
                if (!isCurrent()) return;
                console.error('Error fetching country companies / industries / filter sets / favorites:', error);
            }
        };

        fetchData();
    }, [id]);

    const sortCompaniesByName = () => {
        const newSortOrder = sortOrderName === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const nameA = String(a.title || '').toLowerCase();
            const nameB = String(b.title || '').toLowerCase();
            return newSortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderName(newSortOrder);
    };

    const sortCompaniesByCity = () => {
        const newSortOrder = sortOrderCity === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const cityA = String(a.cityRegion || '').toLowerCase();
            const cityB = String(b.cityRegion || '').toLowerCase();
            return newSortOrder === 'asc' ? cityA.localeCompare(cityB) : cityB.localeCompare(cityA);
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderCity(newSortOrder);
    };

    const sortCompaniesByIndustry = () => {
        const newSortOrder = sortOrderIndustry === 'asc' ? 'desc' : 'asc';
        const getPrimaryIndustryLabel = (company) => {
            const firstIndustryCode = company?.industries?.[0]?.industry;
            if (!firstIndustryCode) return '';
            return industryNameByCode.get(String(firstIndustryCode)) || String(firstIndustryCode);
        };
        refreshSortRef.current = (a, b) => {
            const industryA = getPrimaryIndustryLabel(a).toLowerCase();
            const industryB = getPrimaryIndustryLabel(b).toLowerCase();
            return newSortOrder === 'asc' ? industryA.localeCompare(industryB) : industryB.localeCompare(industryA);
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderIndustry(newSortOrder);
    };

    const sortCompaniesByGlobalSize = () => {
        const newSortOrder = sortOrderGlobalSize === 'asc' ? 'desc' : 'asc';
        const getSize = (value) => {
            const raw = String(value || '').trim().toLowerCase();
            if (!raw || raw === 'n/a' || raw === 'na' || raw === '-') return 0;
            if (raw.includes('several 1000')) return 3000;
            if (raw.includes('under 30')) return 30;
            if (raw.includes('over 100000')) return 100001;
            const rangeMatch = raw.match(/(\d[\d,.\s]*)\s*[-–—]\s*(\d[\d,.\s]*)/);
            if (rangeMatch) {
                const min = Number(rangeMatch[1].replace(/[^\d]/g, ''));
                const max = Number(rangeMatch[2].replace(/[^\d]/g, ''));
                if (!Number.isNaN(min) && !Number.isNaN(max)) return (min + max) / 2;
            }
            const numericValue = Number(raw.replace(/[^\d]/g, ''));
            return Number.isNaN(numericValue) ? 0 : numericValue;
        };
        refreshSortRef.current = (a, b) => {
            const sizeA = getSize(a.globalSize || a.localSize);
            const sizeB = getSize(b.globalSize || b.localSize);
            return newSortOrder === 'asc' ? sizeA - sizeB : sizeB - sizeA;
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderGlobalSize(newSortOrder);
    };

    const sortCompaniesByRating = () => {
        const newSortOrder = sortOrderRating === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const ratingA = Number(a.googleRating) || 0;
            const ratingB = Number(b.googleRating) || 0;
            return newSortOrder === 'asc' ? ratingA - ratingB : ratingB - ratingA;
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderRating(newSortOrder);
    };

    const sortCompaniesByLastUpdate = () => {
        const newSortOrder = sortOrderLastUpdate === 'asc' ? 'desc' : 'asc';
        refreshSortRef.current = (a, b) => {
            const dateA = new Date(a.latestDataUpdate || a.updatedAt || 0).getTime();
            const dateB = new Date(b.latestDataUpdate || b.updatedAt || 0).getTime();
            return newSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
        };
        setCompanies((prev) => [...prev].sort(refreshSortRef.current));
        setSortOrderLastUpdate(newSortOrder);
    };

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    const getPaginationItems = () => {
        const siblingCount = 2;
        const boundaryCount = 1;
        if (totalPages <= 1) return [1];
        const range = (start, end) => {
            const out = [];
            for (let i = start; i <= end; i++) out.push(i);
            return out;
        };
        const startPages = range(1, Math.min(boundaryCount, totalPages));
        const endPages = range(Math.max(totalPages - boundaryCount + 1, boundaryCount + 1), totalPages);
        const siblingsStart = Math.max(
            Math.min(currentPage - siblingCount, totalPages - boundaryCount - siblingCount * 2 - 1),
            boundaryCount + 2
        );
        const siblingsEnd = Math.min(
            Math.max(currentPage + siblingCount, boundaryCount + siblingCount * 2 + 2),
            totalPages - boundaryCount - 1
        );
        const items = [
            ...startPages,
            ...(siblingsStart > boundaryCount + 2 ? ['ellipsis'] : boundaryCount + 1 < totalPages - boundaryCount ? [boundaryCount + 1] : []),
            ...range(siblingsStart, siblingsEnd),
            ...(siblingsEnd < totalPages - boundaryCount - 1 ? ['ellipsis'] : totalPages - boundaryCount > boundaryCount ? [totalPages - boundaryCount] : []),
            ...endPages,
        ];
        return items.filter((v, i) => items.indexOf(v) === i);
    };

    const toggleFavorite = async (companyId) => {
        const companyIdStr = String(companyId);
        const previousFavorites = new Set(favorites);
        const wasFavorited = previousFavorites.has(companyIdStr);
        const optimisticFavorites = new Set(previousFavorites);
        if (wasFavorited) {
            optimisticFavorites.delete(companyIdStr);
        } else {
            optimisticFavorites.add(companyIdStr);
        }
        setFavorites(optimisticFavorites);
        try {
            const res = await api.patch(`/api/users/me/favorite-companies/${companyIdStr}`);
            const safeFavoriteCompanies = Array.isArray(res?.data?.favoriteCompanies)
                ? res.data.favoriteCompanies.map((favId) => String(favId))
                : [];
            setFavorites(new Set(safeFavoriteCompanies));

            const company = companies.find((c) => String(c._id) === companyIdStr);
            trackEvent(
                wasFavorited ? ANALYTICS_EVENTS.FAVORITE_COMPANY_REMOVED : ANALYTICS_EVENTS.FAVORITE_COMPANY_ADDED,
                {
                    companyId: companyIdStr,
                    companyName: company?.title || '',
                    level: 3,
                    source: 'country_companies_list',
                }
            );
        } catch (error) {
            console.error('Error toggling favorite company:', error);
            setFavorites(previousFavorites);
        }
    };

    const captureCurrentMobileFilters = () => captureDiscoveryFilters({
        selectedCity,
        selectedIndustry,
        selectedStudyDirection,
        selectedCompanySize,
        selectedCompanyRating,
        selectedEnglishSpeaking,
        selectedWorldTraveler,
        selectedStudentJobAreas,
        selectedFavorites,
    });

    const restoreMobileFilters = (filters) => {
        setSelectedCity(filters.selectedCity);
        setSelectedIndustry(filters.selectedIndustry);
        setSelectedStudyDirection(filters.selectedStudyDirection);
        setSelectedCompanySize(filters.selectedCompanySize);
        setSelectedCompanyRating(filters.selectedCompanyRating);
        setSelectedEnglishSpeaking(filters.selectedEnglishSpeaking);
        setSelectedWorldTraveler(filters.selectedWorldTraveler);
        setSelectedStudentJobAreas(filters.selectedStudentJobAreas);
        setSelectedFavorites(filters.selectedFavorites);
    };

    const closeMobileFilters = () => {
        restoreMobileFilters(appliedMobileFilters);
        setShowMobileFilters(false);
    };

    const applyMobileFilters = () => {
        setAppliedMobileFilters(captureCurrentMobileFilters());
        setShowMobileFilters(false);
    };

    const clearMobileFiltersPreserveSearch = () => {
        const clearedFilters = captureDiscoveryFilters({});
        restoreMobileFilters(clearedFilters);
        setAppliedMobileFilters(clearedFilters);
    };

    const clearEmptyStateSearchAndFilters = () => {
        clearMobileFiltersPreserveSearch();
        setSearchQuery('');
        setSortField(null);
        setSortDirection(null);
    };

    const removeAppliedMobileFilter = (key) => {
        const booleanKeys = [
            'selectedEnglishSpeaking',
            'selectedWorldTraveler',
            'selectedStudentJobAreas',
            'selectedFavorites',
        ];
        const nextFilters = { ...appliedMobileFilters, [key]: booleanKeys.includes(key) ? null : '' };
        restoreMobileFilters(nextFilters);
        setAppliedMobileFilters(nextFilters);
    };

    const mobileSortOptions = [
        { field: 'name', label: 'Company' },
        { field: 'city', label: 'City' },
        { field: 'openJobs', label: 'Open jobs' },
        { field: 'internships', label: 'Internships' },
        { field: 'companySize', label: 'Company size' },
        { field: 'rating', label: 'Rating' },
        { field: 'lastUpdate', label: 'Last update' },
    ];
    const activeMobileSortOption = mobileSortOptions.find((option) => option.field === sortField);
    const openMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(true);
    };
    const closeMobileSort = () => {
        setDraftSortField(sortField);
        setDraftSortDirection(sortDirection);
        setShowMobileSort(false);
    };
    const clearMobileSort = () => {
        setSortField(null);
        setSortDirection(null);
    };
    const clearMobileSortFromDrawer = () => {
        clearMobileSort();
        setDraftSortField(null);
        setDraftSortDirection(null);
        setShowMobileSort(false);
    };
    const selectDraftSort = (field) => {
        setDraftSortDirection((direction) => (
            draftSortField === field
                ? (direction === 'asc' ? 'desc' : 'asc')
                : mobileCompanySortDefinitions[field].defaultDirection
        ));
        setDraftSortField(field);
    };
    const applyMobileSort = () => {
        setSortField(draftSortField);
        setSortDirection(draftSortDirection);
        setShowMobileSort(false);
    };

    const appliedMobileChips = buildDiscoveryFilterChipLabels(appliedMobileFilters, industryNameByCode)
        .filter((chip) => chip.key !== 'selectedCountry')
        .map((chip) => ({ ...chip, onRemove: () => removeAppliedMobileFilter(chip.key) }));
    if (activeMobileSortOption) {
        appliedMobileChips.push({
            key: 'activeSort',
            label: activeMobileSortOption.label,
            icon: sortDirection === 'asc' ? <FaCaretUp aria-hidden="true" /> : <FaCaretDown aria-hidden="true" />,
            onOpen: openMobileSort,
            onRemove: clearMobileSort,
            removeLabel: `Clear ${activeMobileSortOption.label} sort`,
        });
    }

    const companiesWithCoordinates = filteredCompanies.filter((company) => {
        const lat = Number(company.latitude);
        const lng = Number(company.longitude);
        return !Number.isNaN(lat) && !Number.isNaN(lng);
    });

    const anyCompaniesWithCoordinates = companies.some((company) => {
        const lat = Number(company.latitude);
        const lng = Number(company.longitude);
        return !Number.isNaN(lat) && !Number.isNaN(lng);
    });

    // Opt this page into the shared mobile AI assistant (header trigger +
    // bottom sheet). No structured-filter handler: this page has no AI chip
    // logic today, so the sheet's filter chips stay hidden here.
    useMobileAiPageContext({
        pageType: 'discovery-country-companies',
        mapFirst: true,
        mapScope: 'scoped',
        discoveryType: 'companies',
        countryId: id,
        countryName,
        activeTab: 'Companies',
        suggestions: getDiscoveryAiSuggestions({
            discoveryType: 'companies',
            selectedCountry: countryName,
        }),
    });

    const countryNavItems = getCountryDetailNavItems(id);

    return (
        <>
            <Sidebar />
            <main className={`main-content flex-grow-1 discovery-map-page${isMobile ? ' discovery-companies-page' : ''}${isMobileNavDocked ? ' is-mobile-nav-docked' : ''}`}>
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb countryName={countryName} subPage="Companies" />
                        </Col>
                    </Row>

                    <Row className="mb-4">
                        <Col>
                            <Card className="discovery-shell-card">
                                <Card.Body>
                                    <Row className="discovery-page-title-row">
                                        <Col xs={12}>
                                            <h1>{countryName ? `${countryName}` : 'Companies'}</h1>
                                        </Col>
                                    </Row>

                                    {anyCompaniesWithCoordinates && (
                                        <Row ref={mobileMapRowRef} className="discovery-map-row">
                                            <Col xs={12} className="px-0">
                                                <div className="discovery-mobile-map-area">
                                                    <DiscoveryMap
                                                        items={companiesWithCoordinates}
                                                        height={260}
                                                        markerScale={0.6}
                                                        clusterPixelRadius={20}
                                                        maxClusterZoom={9}
                                                        groupClickZoom={14}
                                                        canCluster={(a, b) => a.country === b.country}
                                                        getLatitude={(company) => company.latitude}
                                                        getLongitude={(company) => company.longitude}
                                                        getTitle={(company) => company.title}
                                                        getLogo={(company) => company.logo || defaultCompanyMarkerLogo}
                                                        getLink={(company) => `/discovery/countries/${id}/companies/${company._id}`}
                                                        onLinkClick={(company) => trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                            companyId: company._id,
                                                            companyName: company.title,
                                                            source: 'country_companies_map',
                                                            level: 3,
                                                            viewMode: 'companies',
                                                        })}
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    {isMobile ? (
                                        <CountryDetailMobileTabs items={countryNavItems} mapPage />
                                    ) : (
                                        <Row className="mb-4">
                                            <Col>
                                                <NavTabs items={countryNavItems} />
                                            </Col>
                                        </Row>
                                    )}

                                    <DiscoveryDesktopAiSlot />

                                    {!isMobile && (
                                        <Row className="mb-3">
                                            <Col xs={12}>
                                                <DiscoveryFilterBar
                                                    title="Companies"
                                                    viewOptions={[
                                                        { value: 'companies', label: 'Companies' },
                                                        { value: 'jobs', label: 'Jobs' },
                                                    ]}
                                                    activeView="companies"
                                                    onViewChange={(view) => {
                                                        if (view === 'jobs') navigate(`/discovery/countries/${id}/jobs`);
                                                    }}
                                                    items={companies}
                                                    industryNameByCode={industryNameByCode}
                                                    showCountryFilter={false}
                                                    cityField="cityRegion"
                                                    searchQuery={searchQuery}
                                                    setSearchQuery={setSearchQuery}
                                                    selectedCity={selectedCity}
                                                    setSelectedCity={setSelectedCity}
                                                    selectedIndustry={selectedIndustry}
                                                    setSelectedIndustry={setSelectedIndustry}
                                                    selectedStudyDirection={selectedStudyDirection}
                                                    setSelectedStudyDirection={setSelectedStudyDirection}
                                                    selectedCompanySize={selectedCompanySize}
                                                    setSelectedCompanySize={setSelectedCompanySize}
                                                    selectedCompanyRating={selectedCompanyRating}
                                                    setSelectedCompanyRating={setSelectedCompanyRating}
                                                    selectedEnglishSpeaking={selectedEnglishSpeaking}
                                                    setSelectedEnglishSpeaking={setSelectedEnglishSpeaking}
                                                    selectedWorldTraveler={selectedWorldTraveler}
                                                    setSelectedWorldTraveler={setSelectedWorldTraveler}
                                                    selectedStudentJobAreas={selectedStudentJobAreas}
                                                    setSelectedStudentJobAreas={setSelectedStudentJobAreas}
                                                    selectedFavorites={selectedFavorites}
                                                    setSelectedFavorites={setSelectedFavorites}
                                                    savedFilterSets={savedFilterSets}
                                                    selectedFilterSet={selectedFilterSet}
                                                    onFilterSetChange={handleFilterSetChange}
                                                    onDeleteFilterSet={handleDeleteFilterSet}
                                                    onSaveFilters={handleSaveFilters}
                                                />
                                            </Col>
                                        </Row>
                                    )}

                                    {isMobile && (
                                        <>
                                            <MobileDiscoveryViewSwitch
                                                activeView="companies"
                                                routes={{
                                                    companies: `/discovery/countries/${id}/companies`,
                                                    jobs: `/discovery/countries/${id}/jobs`,
                                                }}
                                            />

                                            <MobileDiscoveryHeading title="Companies" />

                                            <MobileDiscoverySearchToolbar
                                                searchValue={searchQuery}
                                                onSearchChange={setSearchQuery}
                                                searchPlaceholder="Search..."
                                                searchAriaLabel="Search companies"
                                                actions={[
                                                    {
                                                        label: 'Open filters',
                                                        icon: icFilter,
                                                        onClick: () => setShowMobileFilters(true),
                                                    },
                                                    {
                                                        label: 'Sort companies',
                                                        icon: icSort,
                                                        onClick: openMobileSort,
                                                    },
                                                ]}
                                            />

                                            <MobileAppliedChips chips={appliedMobileChips} />

                                            <DiscoveryFilterBar
                                                title="Companies"
                                                items={companies}
                                                industryNameByCode={industryNameByCode}
                                                showCountryFilter={false}
                                                cityField="cityRegion"
                                                allowedCities={activeCityNames}
                                                searchQuery={searchQuery}
                                                setSearchQuery={setSearchQuery}
                                                selectedCity={selectedCity}
                                                setSelectedCity={setSelectedCity}
                                                selectedIndustry={selectedIndustry}
                                                setSelectedIndustry={setSelectedIndustry}
                                                selectedStudyDirection={selectedStudyDirection}
                                                setSelectedStudyDirection={setSelectedStudyDirection}
                                                selectedCompanySize={selectedCompanySize}
                                                setSelectedCompanySize={setSelectedCompanySize}
                                                selectedCompanyRating={selectedCompanyRating}
                                                setSelectedCompanyRating={setSelectedCompanyRating}
                                                selectedEnglishSpeaking={selectedEnglishSpeaking}
                                                setSelectedEnglishSpeaking={setSelectedEnglishSpeaking}
                                                selectedWorldTraveler={selectedWorldTraveler}
                                                setSelectedWorldTraveler={setSelectedWorldTraveler}
                                                selectedStudentJobAreas={selectedStudentJobAreas}
                                                setSelectedStudentJobAreas={setSelectedStudentJobAreas}
                                                selectedFavorites={selectedFavorites}
                                                setSelectedFavorites={setSelectedFavorites}
                                                savedFilterSets={savedFilterSets}
                                                selectedFilterSet={selectedFilterSet}
                                                onFilterSetChange={handleFilterSetChange}
                                                onDeleteFilterSet={handleDeleteFilterSet}
                                                onSaveFilters={handleSaveFilters}
                                                onClearFilters={clearMobileFiltersPreserveSearch}
                                                mobileModalOnly
                                                mobileModalOpen={showMobileFilters}
                                                onMobileModalClose={closeMobileFilters}
                                                onMobileModalApply={applyMobileFilters}
                                                mobileModalTitle="Filter companies"
                                                mobileApplyLabel="Apply filters"
                                                hideTypeToggleOnMobile
                                                hideActiveFilterSummaryOnMobile
                                                useFullScreenMobilePanel
                                                mobileSavedFilterLabel="Saved filter"
                                                mobileSaveFilterLabel="Save current filters"
                                                compactSavedFilterOnMobile
                                                hideSearchInMobileModal
                                            />
                                        </>
                                    )}

                                    {noCompanies && isMobile ? (
                                        <DiscoveryEmptyState
                                            className="discovery-empty-state--mobile-jobs"
                                            icon={icJobSearch}
                                            title="No matching companies found"
                                            description="Try removing one or more filters or adjust your search."
                                            primaryAction={{
                                                label: 'Clear search & filters',
                                                variant: 'primary',
                                                onClick: clearEmptyStateSearchAndFilters,
                                            }}
                                            secondaryAction={{
                                                label: 'Adjust filters',
                                                variant: 'outline-secondary',
                                                onClick: () => setShowMobileFilters(true),
                                            }}
                                        />
                                    ) : noCompanies ? (
                                        <Row>
                                            <Col md={12}>
                                                <p className="mb-0">No companies available.</p>
                                            </Col>
                                        </Row>
                                    ) : isMobile ? (
                                        <div className="mobile-company-list mobile-company-list--country-detail">
                                            {mobilePagination.mobileItems.map((company) => (
                                                <MobileDiscoveryCompanyCard
                                                    key={company._id}
                                                    company={company}
                                                    isFavorite={favorites.has(String(company._id))}
                                                    onFavoriteToggle={toggleFavorite}
                                                    onOpenCompany={(selectedCompany) => {
                                                        mobilePagination.rememberScrollPosition();
                                                        trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                            companyId: selectedCompany._id,
                                                            companyName: selectedCompany.title,
                                                            source: 'country_companies_list',
                                                            level: 3,
                                                            viewMode: 'companies',
                                                        });
                                                        navigate(`/discovery/countries/${id}/companies/${selectedCompany._id}`);
                                                    }}
                                                />
                                            ))}
                                            <MobileLoadMoreFooter
                                                visibleCount={mobilePagination.visibleCount}
                                                totalCount={mobilePagination.totalCount}
                                                itemLabel="companies"
                                                onLoadMore={mobilePagination.loadMore}
                                                isLoading={mobilePagination.isLoadingMore}
                                                hasMore={mobilePagination.hasMore}
                                            />
                                        </div>
                                    ) : (
                                        <Row>
                                            <Col>
                                                <div className="table-responsive list">
                                                    <Table className="discovery-table">
                                                        <thead>
                                                            <tr>
                                                                <th onClick={sortCompaniesByName} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Company
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortCompaniesByCity} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        City
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortCompaniesByIndustry} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Industry
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortCompaniesByGlobalSize} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Global Size
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th onClick={sortCompaniesByRating} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Rating
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th>
                                                                    <div>Website</div>
                                                                </th>
                                                                <th onClick={sortCompaniesByLastUpdate} style={{ cursor: 'pointer' }}>
                                                                    <div>
                                                                        Last Update
                                                                        <span><FaCaretUp /><FaCaretDown /></span>
                                                                    </div>
                                                                </th>
                                                                <th>Fav</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {currentCompanies.map((company) => (
                                                                <tr
                                                                    className="company-row"
                                                                    key={company._id}
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => {
                                                                        trackEvent(ANALYTICS_EVENTS.COMPANY_OPENED, {
                                                                            companyId: company._id,
                                                                            companyName: company.title,
                                                                            source: 'country_companies_list',
                                                                            level: 3,
                                                                            viewMode: 'companies',
                                                                        });
                                                                        navigate(`/discovery/countries/${id}/companies/${company._id}`);
                                                                    }}
                                                                >
                                                                    <td>
                                                                        <div>
                                                                            <img
                                                                                src={company.logo ? company.logo : defaultLogo}
                                                                                alt={company.title}
                                                                                className="round"
                                                                            />
                                                                            <span className="text-ellipsis text-ellipsis-country-name">
                                                                                <strong>{company.title}</strong>
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td>{company.cityRegion || 'n/a'}</td>
                                                                    <td>
                                                                        {Array.isArray(company.industries) && company.industries.length > 0
                                                                            ? (industryNameByCode.get(String(company.industries[0]?.industry)) ||
                                                                                company.industries[0]?.industry ||
                                                                                'n/a')
                                                                            : 'n/a'}
                                                                    </td>
                                                                    <td>{company.globalSize || 'n/a'}</td>
                                                                    <td>
                                                                        {company.googleRating > 0 ? (
                                                                            <span className="d-inline-flex align-items-center gap-1">
                                                                                {company.googleRating.toFixed(1)}
                                                                                <img src={icStar} alt="rating" style={{ width: '14px', height: '14px' }} />
                                                                            </span>
                                                                        ) : '—'}
                                                                    </td>
                                                                    <td>
                                                                        {company.url ? (
                                                                            <a
                                                                                href={company.url.startsWith('http') ? company.url : `https://${company.url}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                className="d-inline-flex align-items-center"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    trackEvent(ANALYTICS_EVENTS.COMPANY_WEBSITE_CLICKED, {
                                                                                        companyId: company._id,
                                                                                        companyName: company.title,
                                                                                        urlType: 'website',
                                                                                        level: 3,
                                                                                        source: 'country_companies_list',
                                                                                    });
                                                                                }}
                                                                            >
                                                                                <img src={icLink} alt="website" style={{ width: '16px', height: '16px' }} />
                                                                            </a>
                                                                        ) : '—'}
                                                                    </td>
                                                                    <td>
                                                                        {company.latestDataUpdate || company.updatedAt
                                                                            ? new Date(company.latestDataUpdate || company.updatedAt).toLocaleDateString()
                                                                            : '—'}
                                                                    </td>
                                                                    <td>
                                                                        <span
                                                                            className="cursor-pointer d-inline-flex align-items-center"
                                                                            style={{ cursor: 'pointer' }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleFavorite(company._id);
                                                                            }}
                                                                        >
                                                                            <img
                                                                                src={favorites.has(String(company._id)) ? icHeartFull : icHeartEmpty}
                                                                                alt="favorite"
                                                                                style={{ width: '16px', height: '16px' }}
                                                                            />
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    {!isMobile && <Row className="pagination-wrapper">
                                        <Col className="d-flex align-items-center">
                                            <span>
                                                Total {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'}
                                            </span>
                                            <Pagination>
                                                <Pagination.Prev
                                                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                                    disabled={currentPage === 1}
                                                >
                                                    <BsChevronLeft />
                                                </Pagination.Prev>
                                                {getPaginationItems().map((item, index) =>
                                                    item === 'ellipsis' ? (
                                                        <Pagination.Ellipsis key={`e-${index}`} disabled />
                                                    ) : (
                                                        <Pagination.Item
                                                            key={item}
                                                            active={item === currentPage}
                                                            onClick={() => handlePageChange(item)}
                                                        >
                                                            {item}
                                                        </Pagination.Item>
                                                    )
                                                )}
                                                <Pagination.Next
                                                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                                    disabled={currentPage === totalPages || totalPages === 0}
                                                >
                                                    <BsChevronRight />
                                                </Pagination.Next>
                                            </Pagination>
                                        </Col>

                                        <Col className="pagination-controls">
                                            <div className="pagination-controls-per-page">
                                                <Form.Select
                                                    value={companiesPerPage}
                                                    onChange={(e) => {
                                                        setCompaniesPerPage(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    <option value="10">10 / page</option>
                                                    <option value="20">20 / page</option>
                                                    <option value="40">40 / page</option>
                                                </Form.Select>
                                            </div>

                                            <label className="pagination-controls-goto-label" htmlFor="goto-page-country-companies">
                                                Go to
                                            </label>

                                            <Form.Control
                                                id="goto-page-country-companies"
                                                className="pagination-controls-goto-input"
                                                type="number"
                                                value={currentPage}
                                                onChange={(e) => {
                                                    const newPage = Number(e.target.value);
                                                    if (newPage >= 1 && newPage <= totalPages) {
                                                        setCurrentPage(newPage);
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (currentPage < 1) setCurrentPage(1);
                                                    if (currentPage > totalPages) setCurrentPage(totalPages || 1);
                                                }}
                                            />
                                        </Col>
                                    </Row>}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </main>

            <Modal
                className="with-form discovery-filter-mobile-modal discovery-filter-mobile-modal--fullscreen discovery-sort-mobile-modal"
                show={showMobileSort}
                onHide={closeMobileSort}
            >
                <div className="discovery-filter-mobile-panel">
                    <div className="discovery-filter-mobile-panel__header">
                        <Modal.Title>Sort companies</Modal.Title>
                        <div className="discovery-filter-mobile-panel__header-actions">
                            <button type="button" className="discovery-filter-mobile-panel__close" onClick={closeMobileSort} aria-label="Close sort companies">
                                &times;
                            </button>
                        </div>
                    </div>
                    <Modal.Body>
                        <div className="discovery-sort-mobile-options" role="radiogroup" aria-label="Sort companies by">
                            {mobileSortOptions.map((option) => {
                                const isActive = draftSortField === option.field;
                                return (
                                    <button
                                        key={option.field}
                                        type="button"
                                        role="radio"
                                        className={`discovery-sort-mobile-option${isActive ? ' is-active' : ''}`}
                                        onClick={() => selectDraftSort(option.field)}
                                        aria-checked={isActive}
                                    >
                                        <span className="discovery-sort-mobile-option__label">
                                            <span className="discovery-sort-mobile-option__radio" aria-hidden="true" />
                                            <span>{option.label}</span>
                                        </span>
                                        {isActive && (
                                            <span className="discovery-sort-mobile-option__direction" aria-label={draftSortDirection === 'asc' ? 'Ascending' : 'Descending'}>
                                                <span>{draftSortDirection === 'asc' ? 'Ascending' : 'Descending'}</span>
                                                {draftSortDirection === 'asc' ? <FaCaretUp aria-hidden="true" /> : <FaCaretDown aria-hidden="true" />}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </Modal.Body>
                    <div className="discovery-filter-mobile-panel__footer">
                        <Button type="button" variant="outline-secondary" className="discovery-filter-mobile-panel__reset" onClick={clearMobileSortFromDrawer}>
                            Clear sort
                        </Button>
                        <Button type="button" variant="primary" className="discovery-filter-mobile-panel__apply" onClick={applyMobileSort}>
                            Apply sort
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default DiscoveryCountryCompanyList;
